import cn from 'classnames';
import { MdVerified } from 'react-icons/md';
import { InteractiveDiv } from '../../../Components/InteractiveEl';
import {
  DeficiencySeverity,
  DeficiencyStat,
  computeDeficiencySummary
} from './deficiencies-constants';
import { AuditedProduct } from './scoring-engine-constants';

/**
 * Above this portfolio size the per-ASIN cell strip switches from discrete cells
 * (one hover-able cell per listing) to a proportional density bar — below it cells
 * stay individually inspectable, above it they'd be too thin to hover meaningfully.
 */
const CELL_STRIP_MAX = 40;

interface CellStripProps {
  products: AuditedProduct[];
  affectedIds: Set<string>;
  isCritical: boolean;
}

/**
 * A left-to-right fill bar: the first N of `total` cells are filled (N = affected count),
 * read like a normal count bar rather than mapping cells to ASIN positions. Above
 * CELL_STRIP_MAX cells it collapses to a single proportional bar. Filled cells carry the
 * affected ASINs (in order) as hover tooltips.
 */
const CellStrip = ({ products, affectedIds, isCritical }: CellStripProps) => {
  const total = products.length;
  const affectedCount = affectedIds.size;
  const toneClass = isCritical ? 'is-crit' : 'is-warn';

  if (total > CELL_STRIP_MAX) {
    const pct = total === 0 ? 0 : (affectedCount / total) * 100;
    return (
      <div
        className='deficiency-strip is-bar'
        title={`${affectedCount} of ${total} listings affected`}
      >
        <div className={cn('deficiency-strip-fill', toneClass)} style={{ width: `${pct}%` }} />
      </div>
    );
  }

  const affectedAsins = products.filter(p => affectedIds.has(p.id)).map(p => p.asin);
  return (
    <div className='deficiency-strip' aria-label={`${affectedCount} of ${total} listings affected`}>
      {Array.from({ length: total }, (_, i) => {
        const hit = i < affectedCount;
        return (
          <span
            key={i}
            aria-hidden='true'
            title={hit ? affectedAsins[i] : undefined}
            className={cn('deficiency-cell', { [toneClass]: hit })}
          />
        );
      })}
    </div>
  );
};

interface DeficiencyRowProps {
  stat: DeficiencyStat;
  products: AuditedProduct[];
  isSelected: boolean;
  onSelect: () => void;
}

const DeficiencyRow = ({ stat, products, isSelected, onSelect }: DeficiencyRowProps) => {
  const { filter, count, affectedIds } = stat;
  const isCritical = filter.severity === DeficiencySeverity.ERROR;

  return (
    <InteractiveDiv
      className={cn('deficiency-row', {
        'is-selected': isSelected,
        'is-crit': isCritical,
        'is-warn': !isCritical
      })}
      aria-pressed={isSelected}
      onClick={onSelect}
    >
      <div className='deficiency-row-label'>
        <span className='deficiency-row-dot' />
        <span className='deficiency-row-name'>{filter.shortLabel}</span>
        <span className='deficiency-row-threshold'>{filter.thresholdText}</span>
      </div>
      <div className='deficiency-row-count'>
        {count}
        <span className='deficiency-row-of'> / {products.length}</span>
      </div>
      <div className='deficiency-row-strip'>
        <CellStrip products={products} affectedIds={affectedIds} isCritical={isCritical} />
      </div>
    </InteractiveDiv>
  );
};

interface DeficienciesPanelProps {
  products: AuditedProduct[];
  activeFilterId: string | null;
  onSelectFilter: (filterId: string | null) => void;
}

export const DeficienciesPanel = ({
  products,
  activeFilterId,
  onSelectFilter
}: DeficienciesPanelProps) => {
  const { flagged, passing, issueInstances, affectedListings, totalChecks } =
    computeDeficiencySummary(products);

  return (
    <div className='deficiencies-panel'>
      <div className='deficiencies-panel-header'>
        <div>
          <h3>Portfolio Bottlenecks</h3>
          {flagged.length === 0 ? (
            <p className='deficiencies-headline'>
              All {totalChecks} checks passing — no structural bottlenecks detected.
            </p>
          ) : (
            <p className='deficiencies-headline'>
              <strong>
                {flagged.length} of {totalChecks} checks flagged
              </strong>{' '}
              · {issueInstances} issue {issueInstances === 1 ? 'instance' : 'instances'} across{' '}
              {affectedListings} {affectedListings === 1 ? 'listing' : 'listings'} — each cell is
              one ASIN
            </p>
          )}
        </div>
        <div className='deficiencies-legend' aria-hidden='true'>
          <span>
            <i className='swatch-crit' />
            Critical
          </span>
          <span>
            <i className='swatch-warn' />
            Warning
          </span>
          <span>
            <i className='swatch-pass' />
            Passing
          </span>
        </div>
      </div>

      {flagged.length > 0 && (
        <div className='deficiencies-rows'>
          {flagged.map(stat => (
            <DeficiencyRow
              key={stat.filter.id}
              stat={stat}
              products={products}
              isSelected={activeFilterId === stat.filter.id}
              onSelect={() =>
                onSelectFilter(activeFilterId === stat.filter.id ? null : stat.filter.id)
              }
            />
          ))}
        </div>
      )}

      {passing.length > 0 && (
        <div className={cn('deficiencies-passed', { 'has-divider': flagged.length > 0 })}>
          <span className='deficiencies-passed-label'>
            <MdVerified />
            {passing.length} {passing.length === 1 ? 'check' : 'checks'} passing portfolio-wide
          </span>
          <div className='deficiencies-passed-chips'>
            {passing.map(filter => (
              <span key={filter.id} className='deficiencies-passed-chip'>
                {filter.shortLabel}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
